import { useEffect, useState, type ReactNode } from 'react';

import { cn } from '~/utils/cn';

import Header from '../../molecules/Header/view';
import { Icon } from '../../atoms/Icon/view';
import type { propsIconsNames } from '../../atoms/Icon/interfaces';

interface PropsScreen {
  children: ReactNode;
  showHeader?: boolean;
  headerTitle?: string;
  headerSubtitle?: string;
  headerShowBack?: boolean;
  headerRightIcon?: propsIconsNames;
  onClickHeaderRight?: () => void;
  headerRightSlot?: ReactNode;
  hasPadding?: boolean;
  hasScroll?: boolean;
  hasTabNavigation?: boolean;
  screen?: string;
  refreshLoading?: boolean;
  handleRefresh?: () => void;
  className?: string;
  bgClassName?: string;
}

export default function Screen({
  children,
  showHeader = true,
  headerTitle,
  headerSubtitle,
  headerShowBack = true,
  headerRightIcon,
  onClickHeaderRight,
  headerRightSlot,
  hasPadding = true,
  hasScroll = true,
  hasTabNavigation = false,
  refreshLoading,
  handleRefresh,
  className,
  bgClassName,
}: PropsScreen) {
  const [pulling, setPulling] = useState(false);

  useEffect(() => {
    if (!handleRefresh) return;
    let startY = 0;
    const onStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };
    const onMove = (e: TouchEvent) => {
      const delta = e.touches[0].clientY - startY;
      if (delta > 80 && window.scrollY === 0) setPulling(true);
    };
    const onEnd = async () => {
      if (pulling) {
        setPulling(false);
        await handleRefresh();
      }
    };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
  }, [pulling, handleRefresh]);

  return (
    <div
      className={cn(
        'min-h-screen flex flex-col',
        bgClassName ?? 'bg-intense-02',
        hasTabNavigation && 'pb-[80px]',
      )}
    >
      {showHeader ? (
        <Header
          title={headerTitle}
          subtitle={headerSubtitle}
          showBack={headerShowBack}
          rightIcon={headerRightIcon}
          onClickRight={onClickHeaderRight}
          rightSlot={headerRightSlot}
        />
      ) : null}

      {(refreshLoading || pulling) && handleRefresh ? (
        <div className="flex items-center justify-center py-quark">
          <Icon name="icon-loader" size={18} color="#1D4733" className="animate-spin" rounded={false} />
        </div>
      ) : null}

      <main
        className={cn(
          'flex-1 w-full mx-auto xl:max-w-[1200px]',
          hasPadding && 'px-small md:px-medium',
          hasScroll ? '' : 'overflow-hidden',
          className,
        )}
      >
        {children}
      </main>
    </div>
  );
}
